import React, { useState } from 'react';
import {
  Button, Col, Row, Input, Spin,
  Table, Form,
  DatePicker,
  notification,
  Tooltip,
} from 'antd';
import { useNavigate } from 'react-router-dom';

import './ServiceInvoice.scss';
import {
  PlusOutlined, FilePdfOutlined, EyeOutlined, FileTextOutlined,
} from '@ant-design/icons';

import moment from 'moment';
import FilterIcon from 'mdi-react/FilterIcon';
import {
  patch, RequestWrapper, ResponseWrapper, isSuperAgentError, get,
} from '../../../dataSource/requests';

import ModalForm from './ModalForm';
import { MONTH } from '../../../utils/constants';
import { downloadArchive } from '../../../utils/helpers';

interface TablePagination {
  current: number | undefined,
  defaultCurrent: number | undefined,
  pageSize: number | undefined,
  showQuickJumper: boolean | {
    goButton?: React.ReactNode;
  },
  total: number | undefined,
  itemsInPage?: number | undefined,
}

type TInvoice = {
  nfeNumber?: string,
  nfeValue?: string,
  bp: string,
  companyName: string,
  emissionDate: string,
  id: number,
  invoiceNumber: string,
  cooperativeName: string,
  pdf: number,
  xml: number,
  pdfName: string
  xmlName: string
}

type TFilterInvoice = {
  nfeNumber?: string,
  nfeValue?: string,
  bp: string,
  companyName?: string,
  invoiceNumber?: string,
  cooperativeName?: string,
  month: string,
  year: string
}

const ServiceInvoice = (): JSX.Element => {
  const navigate = useNavigate();

  const [formInstance] = Form.useForm();

  const [loading, setLoading] = useState<boolean>(false);
  const [tableDataSource, setTableDataSource] = useState<Array<TInvoice[]>>([]);
  const [tableLoading, setTableLoading] = useState<boolean>(false);
  const [tablePagination, setTablePagination] = useState<TablePagination>({
    current: 1,
    defaultCurrent: 1,
    pageSize: 15,
    showQuickJumper: true,
    total: 0,
  });

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [serviceInvoiceView, setServiceInvoiceView] = useState<TInvoice>({}as TInvoice);

  const formaterFilter = ():TFilterInvoice => {
    const values = formInstance.getFieldsValue();

    return {
      ...values,
      month: values.month ? MONTH[moment(values.month).month()] : '',
      year: values.year ? moment(values.year).year().toString() : '',
    };
  };

  const executeFilter = async (args: {
    page?: number,
    pageSize?: number,
    orderBy?: string,
    orderByDir?: string,
  } = {
    page: 0,
    pageSize: 15,
    orderBy: 'id',
    orderByDir: 'DESC',
  }): Promise<void> => {
    setTableLoading(true);
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;

    const values = formaterFilter();

    const argsQuery = {
      orderBy: ('orderBy' in args) ? args.orderBy : 'id',
      page: ('page' in args && args.page >= 1) ? args?.page : 0,
      pageSize: ('pageSize' in args) ? args.pageSize : 15,
      sort: ('orderByDir' in args) ? args.orderByDir : 'DESC',
    };

    const requestReference: RequestWrapper = patch({
      url: '/invoice/coopercitrus/listAll',
      params: {
        ...Object.fromEntries(
          Object
            .entries(values)
            .filter(
              ([, elementValue]) => {
                return elementValue != null;
              },
            ),
        ),
      },
      query: {
        ...Object.fromEntries(
          Object
            .entries(argsQuery)
            .filter(
              ([, elementValue]) => {
                return elementValue != null;
              },
            ),
        ),
      },
      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      }
    }

    switch (true) {
      case (response != null && response?.body?.code === 23002): {
        const pagination = tablePagination;
        pagination.pageSize = response?.body?.paginationInfo?.maxSize;
        pagination.current = response?.body?.paginationInfo?.pageNumber + 1;
        pagination.total = response?.body?.paginationInfo?.totalElements;

        setTableDataSource(response?.body?.invoiceSapOutput);

        setTablePagination((previous) => {
          return ({
            ...previous,
            current: pagination.current,
            defaultCurrent: pagination.defaultCurrent,
            pageSize: pagination.pageSize,
            showQuickJumper: pagination.showQuickJumper,
            total: pagination.total,
          });
        });
        break;
      }

      case (response?.body?.code === -25004): {
        setTableDataSource([]);
        notification.info({
          message: 'Erro',
          description: 'Não existem notas fiscais para os filtros informados.',
        });
        break;
      }

      case (responseError?.body?.apiResponse?.code === -25005): {
        notification.error({
          message: 'Conexão com SAP',
          description: 'SAP excedeu o tempo limite de resposta para solicitação',
        });
        break;
      }

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao pesquisar, por favor tente novamente mais tarde.',
        });
        break;
    }

    setTableLoading(false);
  };

  const handleTableChange = (pagination: any, _filters: any, sorter: any): void => {
    setTableLoading(true);

    setTablePagination((previous) => {
      return ({
        ...previous,
        current: pagination.current || previous.current,
        defaultCurrent: pagination.defaultCurrent || previous.defaultCurrent,
        pageSize: pagination.pageSize || previous.pageSize,
        showQuickJumper: pagination.showQuickJumper
          || previous.showQuickJumper,
        total: pagination.total || previous.total,
      });
    });

    let finalOrderDir;

    if (sorter?.field && sorter?.order) {
      switch (true) {
        case (sorter.order === 'descend'):
          finalOrderDir = 'DESC';
          break;

        case (sorter.order === 'ascend'):
        case (sorter.order == null):
        default:
          finalOrderDir = 'ASC';
          break;
      }
    }

    executeFilter({
      page: (pagination?.current)
        ? pagination?.current - 1
        : 0,
      orderBy: sorter?.field || 'id',
      orderByDir: finalOrderDir || 'DESC',
      pageSize: pagination.pageSize,
    });
  };

  const dowloadFile = async (type:string, fileId:number):Promise<void> => {
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;

    setLoading(true);

    const requestReference: RequestWrapper = get({
      url: `/invoice/downloadNfeFile/${fileId}`,
      frontConfigs: {
        navigateFunction: navigate,
      },
      headers: {
        'Content-Type': 'application/json',
        Accept: '*/*',
      },
      responseType: 'blob',
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      }
    }

    switch (true) {
      case (response !== null && response?.status === 200):
        downloadArchive(response?.body, response?.type, `nota-${moment().format('yyyy-MM-DD')}`);
        break;

      case (responseError?.code === -9002): {
        notification.error({
          message: 'Erro',
          description: 'O pdf da nota fiscal não foi encontrado.',
        });
        break;
      }

      case (responseError?.body?.apiResponse?.code === -25005): {
        notification.error({
          message: 'Conexão com SAP',
          description: 'SAP excedeu o tempo limite de resposta para solicitação',
        });
        break;
      }

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro. Por favor tente novamente mais tarde.',
        });
        break;
    }

    setLoading(false);
  };

  return (
    <div className="ServiceInvoice">
      <Spin
        tip="Carregando..."
        spinning={loading}
      >
        <Row gutter={[24, 24]}>
          <Col xs={{ span: 24 }}>
            <Row
              gutter={[24, 24]}
              justify="space-between"
              className="title-container"
            >
              <Col>
                <span className="page-title">
                  Notas Fiscais de Serviços
                </span>
              </Col>
            </Row>
          </Col>
        </Row>
        <Form
          scrollToFirstError
          layout="vertical"
          form={formInstance}
          onFinish={
          async (): Promise<void> => {
            await executeFilter();
          }
        }
        >
          <Row
            gutter={24}
            className="filters-container"
          >
            <Col xs={{ span: 24 }} lg={{ span: 24 }}>
              <Row gutter={[8, 24]} className="inputsForm">
                <Col xs={{ span: 24 }} lg={{ span: 6 }}>
                  <Form.Item
                    label="BP do cooperado"
                    name="bp"
                    rules={[{
                      required: true,
                      message: 'Campo obrigatório',
                    }]}
                  >
                    <Input
                      style={{ width: '100%' }}
                      placeholder="Digite o BP do cooperado"
                    />
                  </Form.Item>
                </Col>
                <Col xs={{ span: 24 }} lg={{ span: 6 }}>
                  <Form.Item
                    label="Ano do faturamento"
                    name="year"
                    rules={
                      [{
                        required: true,
                        message: 'Campo obrigatório',
                      }]
                    }
                  >
                    <DatePicker picker="year" style={{ width: '100%' }} format="YYYY" />
                  </Form.Item>
                </Col>
                <Col xs={{ span: 24 }} lg={{ span: 6 }}>
                  <Form.Item
                    label="Mês do faturamento"
                    name="month"
                    rules={
                      [{
                        required: true,
                        message: 'Campo obrigatório',
                      }]
                    }
                  >
                    <DatePicker picker="month" style={{ width: '100%' }} format="MMMM" />
                  </Form.Item>
                </Col>
                <Col xs={{ span: 24 }} lg={{ span: 6 }}>
                  <Form.Item
                    label="Número do faturamento"
                    name="invoiceNumber"
                    rules={[]}
                  >
                    <Input
                      type="number"
                      min={0}
                      style={{ width: '100%' }}
                      placeholder="Digite o número do faturamento"
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={[8, 24]} className="inputsForm">
                <Col xs={{ span: 24 }} lg={{ span: 8 }}>
                  <Form.Item
                    label="Nome da propriedade"
                    name="cooperativeName"
                    rules={[]}
                  >
                    <Input
                      style={{ width: '100%' }}
                      placeholder="Digite o nome da propriedade"
                    />
                  </Form.Item>
                </Col>
                <Col xs={{ span: 24 }} lg={{ span: 8 }}>
                  <Form.Item
                    label="Nome da empresa emissora"
                    name="companyName"
                    rules={[]}
                  >
                    <Input
                      style={{ width: '100%' }}
                      placeholder="Digite o nome da empresa emissora"
                    />
                  </Form.Item>
                </Col>
                <Col xs={{ span: 24 }} lg={{ span: 8 }}>
                  <Form.Item label=" ">
                    <Button
                      className="btn-filter"
                      type="primary"
                      htmlType="submit"
                    >
                      <FilterIcon />
                      Filtrar
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Col>
          </Row>
        </Form>
        {tableDataSource.length > 0 && (
          <Row gutter={24} className="list-container">
            <Col span={24}>
              <Row gutter={24}>
                <Col span={24}>
                  <Table
                    className="AccountDeletionRequests__Table"
                    columns={[
                      {
                        title: <div className="table-titles">Nome da propriedade</div>,
                        dataIndex: 'cooperativeName',
                        key: 'cooperativeName',
                        ellipsis: true,
                        sorter: true,
                        defaultSortOrder: 'descend',
                        width: 200,
                        render: (
                          _value: string | number,
                          record: any,
                        ): React.ReactNode => {
                          if (record.cooperativeName) {
                            return record.cooperativeName;
                          }
                          return '-';
                        },
                      },
                      {
                        title: <div className="table-titles">Nome da empresa emissora</div>,
                        dataIndex: 'companyName',
                        key: 'companyName',
                        ellipsis: true,
                        sorter: true,
                        defaultSortOrder: 'descend',
                        width: 250,
                      },
                      {
                        title: <div className="table-titles">Data de emissão</div>,
                        dataIndex: 'emissionDate',
                        key: 'emissionDate',
                        ellipsis: true,
                        sorter: true,
                        defaultSortOrder: 'descend',
                        width: 200,
                        render: (
                          _value: string | number,
                          record: any,
                        ): React.ReactNode => {
                          if (record.emissionDate) {
                            return moment(record.emissionDate).utc().format('DD/MM/YYYY');
                          }
                          return '-';
                        },
                      },
                      {
                        title: <div className="table-titles">Valor do faturamento</div>,
                        dataIndex: 'nfeValue',
                        key: 'nfeValue',
                        ellipsis: true,
                        sorter: true,
                        defaultSortOrder: 'descend',
                        width: 200,
                      },
                      {
                        title: <div className="table-titles">Número do faturamento</div>,
                        key: 'invoiceNumber',
                        dataIndex: 'invoiceNumber',
                        ellipsis: true,
                        sorter: true,
                        width: 225,
                      },
                      {
                        title: <div className="table-titles">Número da nota fiscal</div>,
                        key: 'nfeNumber',
                        dataIndex: 'nfeNumber',
                        ellipsis: true,
                        sorter: true,
                        width: 225,
                        render: (
                          _value: string | number,
                          record: any,
                        ): React.ReactNode => {
                          if (record.nfeNumber) {
                            return record.nfeNumber;
                          }
                          return '-';
                        },
                      },
                      {
                        title: <div className="table-titles">PDF</div>,
                        dataIndex: 'pdf',
                        key: 'pdf',
                        ellipsis: true,
                        sorter: true,
                        width: 80,
                        render: (
                          _value: string | number,
                          record: any,
                        ): React.ReactNode => {
                          if (record.pdf && record.pdfName) {
                            return (
                              <Tooltip title="Baixar arquivo PDF">
                                <span>
                                  <Button
                                    icon={<FilePdfOutlined />}
                                    onClick={
                                  async (): Promise<void> => {
                                    await dowloadFile('pdf', record.pdf);
                                  }
                                }
                                  />
                                </span>
                              </Tooltip>
                            );
                          }
                          return '-';
                        },
                      },
                      {
                        title: <div className="table-titles">XML</div>,
                        dataIndex: 'xml',
                        key: 'xml',
                        ellipsis: true,
                        sorter: true,
                        width: 80,
                        render: (
                          _value: string | number,
                          record: any,
                        ): React.ReactNode => {
                          if (record.xml && record.xmlName) {
                            return (
                              <Tooltip title="Baixar arquivo XML">
                                <span>
                                  <Button
                                    icon={<FileTextOutlined />}
                                    onClick={
                                  async (): Promise<void> => {
                                    await dowloadFile('xml', record.xml);
                                  }
                                }
                                  />
                                </span>
                              </Tooltip>
                            );
                          }
                          return '-';
                        },
                      },
                      {
                        title: <div className="table-titles">Ação</div>,
                        dataIndex: 'id',
                        key: 'acoes',
                        render: (
                          _value: string | number,
                          record: any,
                        ): React.ReactNode => (
                          <span className="btn-actions">
                            {record.nfeNumber ? (
                              <Button
                                icon={<EyeOutlined />}
                                onClick={(): void => {
                                  setServiceInvoiceView(record);
                                  setOpenModal(true);
                                }}
                              >
                                Visualizar
                              </Button>
                            ) : (
                              <Button
                                icon={<PlusOutlined />}
                                onClick={(): void => {
                                  setServiceInvoiceView(record);
                                  setOpenModal(true);
                                }}
                              >
                                Cadastrar
                              </Button>
                            )}
                          </span>
                        ),
                        width: 170,
                      },
                    ]}
                    dataSource={tableDataSource}
                    loading={tableLoading}
                    rowKey={(record): string => record.id as unknown as string}
                    pagination={{
                      defaultCurrent: tablePagination.defaultCurrent || 1,
                      current: tablePagination.current || 1,
                      pageSize: tablePagination.pageSize || 15,
                      total: tablePagination.total || 0,
                      showQuickJumper: tablePagination.showQuickJumper || true,
                      pageSizeOptions: ['1', '15', '50', '100'],
                      showSizeChanger: true,
                    }}
                    onChange={handleTableChange}
                    scroll={{
                      x: '100vw',
                    }}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        )}
      </Spin>
      <ModalForm
        open={openModal}
        modalStatusChange={setOpenModal}
        invoice={serviceInvoiceView}
        updateTable={executeFilter}
      />
    </div>
  );
};

export default ServiceInvoice;
